/**
 * @graphql cc
 */
import { useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { calculateEstimatedReadTime, getAdf, getWordCount } from '../readTimeUtils.ts';
import { extractContentFeatures, extractContentFeaturesSkipExpand } from '../featuresExtractor.ts';
import { ReadTimeQuery } from '../ReadTimeQuery.graphql.ts';

export const useReadTime = (contentId: string, skipQuery?: boolean) => {
	// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations, graphql-client-usage/graphql-generic-types -- Preloaded GraphQL Operation
	const { data, loading, error } = useQuery(ReadTimeQuery, {
		variables: {
			contentId,
		},
		fetchPolicy: 'cache-only',
		skip: skipQuery,
	});

	const { adf, readTime, wordCount, features, featuresSkipExpand } = useMemo(() => {
		const adfFromData = getAdf(data);

		if (!adfFromData || loading || error || !contentId) {
			return {
				readTime: null,
				wordCount: null,
				features: null,
				featuresSkipExpand: null,
			};
		}

		return {
			adf: adfFromData,
			readTime: calculateEstimatedReadTime(adfFromData),
			wordCount: getWordCount(adfFromData),
			features: extractContentFeatures(adfFromData),
			featuresSkipExpand: extractContentFeaturesSkipExpand(adfFromData),
		};
	}, [loading, error, data, contentId]);

	const contentNode = data?.content?.nodes?.[0];
	const contentType = contentNode?.type;

	return {
		loading,
		error,
		adf,
		contentType,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
	};
};
